/**
 * Discover — local-first front door.
 *
 * Anonymous visitors and members land here. It answers:
 *   "What's happening near me for my dog this week, this month, ever?"
 *
 * Data comes from /api/discover which aggregates events, adoption
 * agencies, and NGO partners geo-ranked to the caller's location.
 * The location pill is user-overridable and cached in localStorage.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  MapPin, Calendar, Heart, Users, ExternalLink,
  Search, Loader2, Plus, Store, List, Map as MapIcon,
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'aadc-discover-location';
const VIEW_STORAGE_KEY = 'aadc-discover-view';

const TOPICS = [
  { key: 'events',        label: 'Events',           icon: Calendar },
  { key: 'adoption',      label: 'Adoption',         icon: Heart },
  { key: 'meetup',        label: 'Meetups',          icon: Users },
  { key: 'training',      label: 'Training',         icon: Users },
  { key: 'ngos',          label: 'Rescues',          icon: Heart },
  { key: 'independents',  label: 'Trusted vets & shops', icon: Store },
];

// ---- Leaflet marker icons (color-coded per category) ------------------
// Uses a data-URL SVG so we don't have to ship extra image assets.
function makeIcon(color) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" viewBox="0 0 30 42">
      <path fill="${color}" stroke="#fff" stroke-width="2"
        d="M15 2C7.8 2 2 7.6 2 14.5 2 24 15 40 15 40s13-16 13-25.5C28 7.6 22.2 2 15 2z"/>
      <circle cx="15" cy="14" r="5" fill="#fff"/>
    </svg>`;
  return new L.Icon({
    iconUrl: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
    iconSize: [30, 42],
    iconAnchor: [15, 42],
    popupAnchor: [0, -36],
  });
}
const ICONS = {
  event: makeIcon('#1E4731'),        // Forest Pine
  agency: makeIcon('#C95A2B'),       // Terracotta
  ngo: makeIcon('#ECA02B'),          // Honey Gold
  independent: makeIcon('#2563eb'),  // Slate Blue (contrast)
  me: makeIcon('#7c3aed'),           // Violet (user location)
};

function loadStoredLocation() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
}

function saveLocation(loc) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(loc)); } catch {}
}

function loadStoredView() {
  try { return localStorage.getItem(VIEW_STORAGE_KEY) || 'list'; } catch { return 'list'; }
}

function saveView(mode) {
  try { localStorage.setItem(VIEW_STORAGE_KEY, mode); } catch {}
}

export default function DiscoverPage() {
  const navigate = useNavigate();
  const { city } = useParams();
  const cityLabel = useMemo(() => {
    if (!city) return null;
    return city
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }, [city]);

  // Imperative title fallback — Helmet's default was winning the LIFO race
  // on this route in testing; setting document.title directly guarantees the
  // browser tab + share cards see the city string.
  useEffect(() => {
    document.title = cityLabel
      ? `Dogs near ${cityLabel} — Events, adoption, trusted vets | AADC`
      : 'Discover — Dog events, adoption, rescues near you | AADC';
  }, [cityLabel]);
  const [location, setLocation] = useState(loadStoredLocation());
  const [selectedTopics, setSelectedTopics] = useState(new Set());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locDialogOpen, setLocDialogOpen] = useState(false);
  const [locSearch, setLocSearch] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [viewMode, setViewMode] = useState(loadStoredView()); // 'list' | 'map'

  const changeView = (mode) => {
    setViewMode(mode);
    saveView(mode);
  };

  const topicsParam = useMemo(() => {
    if (selectedTopics.size === 0) return null;
    return Array.from(selectedTopics).join(',');
  }, [selectedTopics]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (location?.lat && location?.lng) {
        params.lat = location.lat;
        params.lng = location.lng;
      }
      if (topicsParam) params.topics = topicsParam;
      const res = await axios.get(`${API}/discover`, { params });
      setData(res.data);
      if (!location && res.data.location) {
        // First load: adopt server-detected location
        const detected = {
          lat: res.data.location.lat,
          lng: res.data.location.lng,
          label: res.data.location.label,
        };
        setLocation(detected);
        saveLocation(detected);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load nearby items');
    } finally {
      setLoading(false);
    }
  }, [location, topicsParam]);

  useEffect(() => { load(); }, [load]);

  // If the route carries a /:city segment and the stored location doesn't
  // match it, auto-geocode the city so the landing/CTA→city flow lands the
  // visitor on a city-scoped view without an extra click.
  useEffect(() => {
    if (!cityLabel) return;
    const already = (location?.label || '').toLowerCase().startsWith(cityLabel.toLowerCase());
    if (already) return;
    (async () => {
      try {
        const q = encodeURIComponent(cityLabel);
        const r = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&addressdetails=1`,
          { headers: { 'Accept-Language': 'en-US' } },
        );
        const hits = await r.json();
        if (hits && hits.length) {
          const h = hits[0];
          const parts = h.display_name.split(',').map((s) => s.trim());
          const label = parts.slice(0, 2).join(', ');
          const next = { lat: parseFloat(h.lat), lng: parseFloat(h.lon), label };
          setLocation(next);
          saveLocation(next);
        }
      } catch {
        // silent — user can still change location via the pill
      }
    })();
    // Only re-run when the URL city changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityLabel]);

  const toggleTopic = (key) => {
    setSelectedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const applyManualLocation = async () => {
    if (!locSearch.trim()) return;
    setGeocoding(true);
    try {
      // Free Nominatim client-side (respects their AUP: single query, has UA)
      const q = encodeURIComponent(locSearch.trim());
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&addressdetails=1`,
        { headers: { 'Accept-Language': 'en-US' } },
      );
      const hits = await r.json();
      if (hits && hits.length) {
        const h = hits[0];
        const parts = h.display_name.split(',').map((s) => s.trim());
        const label = parts.slice(0, 2).join(', ');
        const next = {
          lat: parseFloat(h.lat),
          lng: parseFloat(h.lon),
          label,
        };
        setLocation(next);
        saveLocation(next);
        setLocDialogOpen(false);
        setLocSearch('');
      } else {
        toast.error('Could not find that place — try "Boston, MA" or a zip code');
      }
    } catch {
      toast.error('Lookup failed — try again in a moment');
    } finally {
      setGeocoding(false);
    }
  };

  const usePreciseLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Your browser does not support precise location');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          label: 'My location',
        };
        setLocation(next);
        saveLocation(next);
        setLocDialogOpen(false);
        toast.success('Using your precise location');
      },
      (err) => {
        toast.error(err.message || 'Location permission denied');
      },
      { enableHighAccuracy: false, timeout: 8000 },
    );
  };

  return (
    <div className="min-h-screen bg-background" data-testid="discover-page">
      <SEO
        title={cityLabel ? `Dogs near ${cityLabel} — Events, adoption, trusted vets` : 'Discover — Dog events, adoption, rescues near you'}
        description={cityLabel
          ? `Local dog events, rescues, adoption agencies, and trusted vets & groomers in and around ${cityLabel}.`
          : 'Find local dog adoption events, meetups, training classes, and rescue partners near you.'}
      />

      {/* Header — location pill + topic chips */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="font-mono uppercase tracking-widest text-[10px] text-primary mb-1">
                {cityLabel ? `${cityLabel} — Local directory` : 'Local directory'}
              </p>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                {cityLabel ? `Dogs near ${cityLabel}` : "What's near you"}
              </h1>
              <button
                onClick={() => setLocDialogOpen(true)}
                className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card px-3 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
                data-testid="location-pill"
              >
                <MapPin className="w-4 h-4 text-primary" />
                <span className="font-medium">{location?.label || 'Detecting…'}</span>
                <span className="text-xs text-muted-foreground ml-1 font-mono uppercase tracking-wider">change</span>
              </button>
            </div>
            <div className="flex items-center gap-2">
              {/* View toggle */}
              <div className="hidden sm:inline-flex rounded-full border border-border/70 bg-card p-0.5" data-testid="view-toggle">
                <button
                  onClick={() => changeView('list')}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full transition ${
                    viewMode === 'list' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="view-list-btn"
                  aria-pressed={viewMode === 'list'}
                >
                  <List className="w-3.5 h-3.5" /> List
                </button>
                <button
                  onClick={() => changeView('map')}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full transition ${
                    viewMode === 'map' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="view-map-btn"
                  aria-pressed={viewMode === 'map'}
                >
                  <MapIcon className="w-3.5 h-3.5" /> Map
                </button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/events')}
                className="rounded-full border-border/70 hover:border-primary hover:text-primary"
                data-testid="create-event-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Add an event
              </Button>
            </div>
          </div>

          {/* Topic chips */}
          <div className="flex items-center gap-2 mt-4 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0">
            {TOPICS.map(({ key, label, icon: Icon }) => {
              const active = selectedTopics.has(key);
              return (
                <button
                  key={key}
                  onClick={() => toggleTopic(key)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider border shrink-0 transition ${
                    active
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card text-muted-foreground border-border/70 hover:border-primary hover:text-primary'
                  }`}
                  data-testid={`topic-chip-${key}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              );
            })}
            {selectedTopics.size > 0 && (
              <button
                onClick={() => setSelectedTopics(new Set())}
                className="text-xs text-muted-foreground hover:text-foreground ml-2 font-mono uppercase tracking-wider shrink-0"
                data-testid="clear-topics-btn"
              >
                clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 sm:pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="w-6 h-6 mr-2 animate-spin text-primary" />
            <span className="font-mono uppercase tracking-widest text-xs">Finding local dog things…</span>
          </div>
        ) : error ? (
          <Card className="border-destructive/40 bg-destructive/5"><CardContent className="p-6 text-destructive">{error}</CardContent></Card>
        ) : viewMode === 'map' ? (
          <DiscoverMap data={data} location={location} />
        ) : (
          <>
            <Section
              title="This week's events"
              emptyLabel="No events this week within 50 miles. Be the first to add one!"
              items={data?.events_this_week || []}
              renderItem={(e) => <EventCard event={e} key={e.id} />}
              cta={{ label: 'Add an event →', onClick: () => navigate('/events') }}
            />

            <Section
              title="More upcoming events nearby"
              emptyLabel=""
              items={data?.nearby_events || []}
              renderItem={(e) => <EventCard event={e} key={e.id} />}
            />

            <Section
              title="Adoption agencies near you"
              emptyLabel="No agencies within 50 miles yet."
              items={data?.adoption_agencies || []}
              renderItem={(a) => <AgencyCard agency={a} key={a.id} />}
            />

            <Section
              title="Partner rescues & NGOs"
              emptyLabel=""
              items={data?.ngos || []}
              renderItem={(n) => <NgoCard ngo={n} key={n.slug} />}
            />

            <Section
              title="Trusted independent vets, shops & trainers"
              emptyLabel=""
              items={data?.trusted_independents || []}
              renderItem={(p) => <IndependentCard provider={p} key={p.id} />}
              cta={{ label: 'Nominate a place →', onClick: () => navigate('/trusted-providers') }}
            />

            {/* Anonymous CTA */}
            {(data?.events_this_week?.length || 0) + (data?.nearby_events?.length || 0) > 0 && (
              <Card className="mt-10 border-primary/20 bg-gradient-to-br from-primary/[0.08] via-accent/[0.08] to-secondary/[0.05]">
                <CardContent className="p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div>
                    <p className="font-mono uppercase tracking-widest text-[10px] text-primary mb-1">Weekly digest</p>
                    <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground">Save this location · get a weekly digest</h3>
                    <p className="text-sm text-muted-foreground mt-1 font-body">
                      One email a week — new events, rescues, and things to know for dog people near you.
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/signup')}
                    className="h-11 rounded-full px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-soft transition-all active:scale-95"
                    data-testid="signup-cta"
                  >
                    Get the digest
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Mobile floating view toggle */}
      <div className="sm:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-40" data-testid="mobile-view-toggle">
        <div className="inline-flex rounded-full bg-foreground text-background shadow-hover p-1">
          <button
            onClick={() => changeView('list')}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-full transition ${
              viewMode === 'list' ? 'bg-background text-foreground' : 'text-background/70'
            }`}
            data-testid="mobile-view-list-btn"
          >
            <List className="w-3.5 h-3.5" /> List
          </button>
          <button
            onClick={() => changeView('map')}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-full transition ${
              viewMode === 'map' ? 'bg-background text-foreground' : 'text-background/70'
            }`}
            data-testid="mobile-view-map-btn"
          >
            <MapIcon className="w-3.5 h-3.5" /> Map
          </button>
        </div>
      </div>

      {/* Location dialog */}
      <Dialog open={locDialogOpen} onOpenChange={setLocDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change location</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-slate-600">City, state or zip</label>
              <Input
                value={locSearch}
                onChange={(e) => setLocSearch(e.target.value)}
                placeholder="Boston, MA  or  02116"
                onKeyDown={(e) => e.key === 'Enter' && applyManualLocation()}
                data-testid="location-search-input"
              />
            </div>
            <Button
              onClick={applyManualLocation}
              disabled={geocoding || !locSearch.trim()}
              className="w-full"
              data-testid="location-apply-btn"
            >
              {geocoding && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Search className="w-4 h-4 mr-2" /> Find it
            </Button>
            <div className="text-center text-xs text-slate-400 py-1">or</div>
            <Button
              variant="outline"
              onClick={usePreciseLocation}
              className="w-full"
              data-testid="location-precise-btn"
            >
              <MapPin className="w-4 h-4 mr-2" /> Use my precise location
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLocDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Section wrapper ---
function Section({ title, emptyLabel, items, renderItem, cta }) {
  if (!items || items.length === 0) {
    if (!emptyLabel) return null;
    return (
      <div className="mb-10">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-3 tracking-tight">{title}</h2>
        <Card className="border-dashed border-border/80 bg-muted/30">
          <CardContent className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-3">
              <Search className="w-5 h-5" />
            </div>
            <p className="text-sm text-muted-foreground font-body">{emptyLabel}</p>
            {cta && (
              <button
                onClick={cta.onClick}
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/70 transition-colors"
              >
                {cta.label}
              </button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground tracking-tight">{title}</h2>
        <Badge variant="outline" className="font-mono uppercase tracking-wider text-[10px] border-border/70 text-muted-foreground">
          {items.length}
        </Badge>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => renderItem(it))}
      </div>
    </div>
  );
}

// --- Cards ---
function EventCard({ event }) {
  const link = `/events/${event.id}`;
  const distance = event.distance_miles;
  const dateStr = event.date && event.time ? `${event.date} · ${event.time}` : event.date;
  return (
    <Link to={link} data-testid={`event-card-${event.id}`} className="group">
      <Card className="h-full border-border/70 bg-card hover:border-primary/40 hover:shadow-hover transition-all duration-300 hover:-translate-y-0.5">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-2">
            <Badge variant="outline" className="bg-primary/8 text-primary border-primary/30 text-[10px] font-mono uppercase tracking-wider">
              {event.event_type || 'event'}
            </Badge>
            {distance != null && (
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{distance} mi</span>
            )}
          </div>
          <h3 className="font-heading font-semibold text-foreground mt-3 line-clamp-2 leading-snug">{event.title}</h3>
          <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-primary/70" /> {dateStr}
          </div>
          {(event.city || event.location) && (
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-primary/70" /> {event.city || event.location}
              {event.state ? `, ${event.state}` : ''}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function AgencyCard({ agency }) {
  return (
    <a
      href={agency.website || `/adoption-agencies/${agency.id}`}
      target={agency.website ? '_blank' : '_self'}
      rel="noreferrer"
      data-testid={`agency-card-${agency.id}`}
      className="group"
    >
      <Card className="h-full border-border/70 bg-card hover:border-secondary/40 hover:shadow-hover transition-all duration-300 hover:-translate-y-0.5">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-2">
            <Badge variant="outline" className="bg-secondary/8 text-secondary border-secondary/30 text-[10px] font-mono uppercase tracking-wider">
              adoption
            </Badge>
            {agency.distance_miles != null && (
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{agency.distance_miles} mi</span>
            )}
          </div>
          <h3 className="font-heading font-semibold text-foreground mt-3 line-clamp-2 leading-snug flex items-center gap-1">
            {agency.name}
            {agency.website && <ExternalLink className="w-3 h-3 text-muted-foreground" />}
          </h3>
          {(agency.city || agency.state) && (
            <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-secondary/70" /> {agency.city}
              {agency.state ? `, ${agency.state}` : ''}
            </div>
          )}
          {agency.description && (
            <p className="text-xs text-muted-foreground mt-2 line-clamp-2 font-body">{agency.description}</p>
          )}
        </CardContent>
      </Card>
    </a>
  );
}

function NgoCard({ ngo }) {
  return (
    <Link to={`/partners/${ngo.slug}`} data-testid={`ngo-card-${ngo.slug}`} className="group">
      <Card className="h-full border-border/70 bg-card hover:border-accent/50 hover:shadow-hover transition-all duration-300 hover:-translate-y-0.5">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-2">
            <Badge variant="outline" className="bg-accent/12 text-accent-foreground border-accent/40 text-[10px] font-mono uppercase tracking-wider">
              rescue
            </Badge>
            {ngo.distance_miles != null && (
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{ngo.distance_miles} mi</span>
            )}
          </div>
          <h3 className="font-heading font-semibold text-foreground mt-3 line-clamp-2 leading-snug">{ngo.name}</h3>
          {ngo.tagline && <p className="text-xs text-muted-foreground mt-2 line-clamp-2 font-body">{ngo.tagline}</p>}
        </CardContent>
      </Card>
    </Link>
  );
}

// --- Trusted Independent card ---
function IndependentCard({ provider }) {
  const OWNERSHIP_LABELS = {
    independent: 'Founder-owned',
    family_owned: 'Family-owned',
    employee_owned: 'Employee-owned',
    cooperative: 'Co-op',
    nonprofit: 'Nonprofit',
    academic: 'University',
  };
  const ownerLabel = OWNERSHIP_LABELS[provider.ownership_type] || 'Independent';
  return (
    <Link to={`/trusted-providers/${provider.slug || provider.id}`} data-testid={`independent-card-${provider.id}`} className="group">
      <Card className="h-full border-border/70 bg-card hover:border-primary/40 hover:shadow-hover transition-all duration-300 hover:-translate-y-0.5">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-2">
            <Badge variant="outline" className="bg-primary/8 text-primary border-primary/30 text-[10px] font-mono uppercase tracking-wider">
              {provider.type}
            </Badge>
            {provider.distance_miles != null && (
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{provider.distance_miles} mi</span>
            )}
          </div>
          <h3 className="font-heading font-semibold text-foreground mt-3 line-clamp-2 leading-snug flex items-center gap-1">
            {provider.name}
            {provider.website && <ExternalLink className="w-3 h-3 text-muted-foreground" />}
          </h3>
          <div className="text-[10px] font-mono uppercase tracking-widest text-primary mt-1">{ownerLabel}</div>
          {(provider.city || provider.state) && (
            <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-primary/70" /> {provider.city}
              {provider.state ? `, ${provider.state}` : ''}
            </div>
          )}
          {provider.service_notes && (
            <p className="text-xs text-muted-foreground mt-2 line-clamp-2 font-body">{provider.service_notes}</p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

// --- Map view ---
//
// Renders every discoverable item as a color-coded pin on an OpenStreetMap
// tile layer. Pins are grouped by category (events amber, agencies rose,
// NGOs emerald, independents blue, user violet). Clicking a pin shows a
// name + a link to the item's detail page. Uses free OSM tiles, no API key.
function FitBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points || points.length === 0) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
  }, [points, map]);
  return null;
}

function DiscoverMap({ data, location }) {
  const events = [...(data?.events_this_week || []), ...(data?.nearby_events || [])];
  const agencies = data?.adoption_agencies || [];
  const ngos = data?.ngos || [];
  const independents = data?.trusted_independents || [];

  const points = [];
  events.forEach((e) => {
    if (e.latitude && e.longitude) {
      points.push({
        key: `e-${e.id}`, lat: e.latitude, lng: e.longitude, icon: ICONS.event,
        title: e.title, subtitle: e.date, link: `/events/${e.id}`, kind: 'Event',
      });
    }
  });
  agencies.forEach((a) => {
    if (a.latitude && a.longitude) {
      points.push({
        key: `a-${a.id}`, lat: a.latitude, lng: a.longitude, icon: ICONS.agency,
        title: a.name, subtitle: `${a.city || ''}${a.state ? ', ' + a.state : ''}`,
        link: a.website || `/adoption-agencies/${a.id}`, external: !!a.website, kind: 'Adoption agency',
      });
    }
  });
  ngos.forEach((n) => {
    if (n.latitude && n.longitude) {
      points.push({
        key: `n-${n.slug}`, lat: n.latitude, lng: n.longitude, icon: ICONS.ngo,
        title: n.name, subtitle: n.tagline || '',
        link: `/partners/${n.slug}`, kind: 'Rescue / NGO',
      });
    }
  });
  independents.forEach((p) => {
    if (p.latitude && p.longitude) {
      points.push({
        key: `i-${p.id}`, lat: p.latitude, lng: p.longitude, icon: ICONS.independent,
        title: p.name, subtitle: `${p.type} · ${p.city || ''}${p.state ? ', ' + p.state : ''}`,
        link: `/trusted-providers/${p.slug || p.id}`, kind: 'Trusted independent',
      });
    }
  });

  const center = location?.lat && location?.lng
    ? [location.lat, location.lng]
    : [39.5, -98.35]; // continental US

  return (
    <div className="rounded-2xl border border-border/70 overflow-hidden bg-card shadow-soft" data-testid="discover-map">
      <div className="h-[560px] w-full">
        <MapContainer center={center} zoom={11} className="h-full w-full">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
          />
          {location?.lat && location?.lng && (
            <Marker position={[location.lat, location.lng]} icon={ICONS.me}>
              <Popup><strong>You are near here</strong><br/>{location.label}</Popup>
            </Marker>
          )}
          {points.map((p) => (
            <Marker key={p.key} position={[p.lat, p.lng]} icon={p.icon}>
              <Popup>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 font-mono">{p.kind}</div>
                <div className="font-heading font-semibold text-foreground">{p.title}</div>
                {p.subtitle && <div className="text-xs text-muted-foreground mt-0.5">{p.subtitle}</div>}
                <a
                  href={p.link}
                  target={p.external ? '_blank' : '_self'}
                  rel="noreferrer"
                  className="inline-block mt-2 text-xs font-semibold text-primary hover:underline"
                  data-testid={`map-popup-link-${p.key}`}
                >
                  Open →
                </a>
              </Popup>
            </Marker>
          ))}
          <FitBounds points={points.concat(location?.lat ? [{ lat: location.lat, lng: location.lng }] : [])} />
        </MapContainer>
      </div>
      <div className="px-4 py-3 border-t border-border/60 flex flex-wrap gap-x-4 gap-y-2 text-xs font-mono uppercase tracking-wider text-muted-foreground bg-muted/30">
        <LegendDot color="#1E4731" label={`Events (${events.filter((e) => e.latitude).length})`} />
        <LegendDot color="#C95A2B" label={`Adoption (${agencies.filter((a) => a.latitude).length})`} />
        <LegendDot color="#ECA02B" label={`Rescues (${ngos.filter((n) => n.latitude).length})`} />
        <LegendDot color="#2563eb" label={`Trusted (${independents.filter((p) => p.latitude).length})`} />
        <LegendDot color="#7c3aed" label="You" />
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block w-2.5 h-2.5 rounded-full"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

